import { ButtonProtected, FullScreenOverlay } from '@components'

import { Color } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BillCreatedEventDetail } from '@features/bills/types/billCreatedEventDetail'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'
import { BillEditable } from '@views/bills/elements/BillEditable/BillEditable'
import type { BillFormPartial } from '@views/bills/elements/BillEditableForm/utils/formData'

import { queryClient } from '../../../../../../../../config/queryClient'
import { CustomEvent } from '../../../../../../../../enums/customEvent'
import { QueryKeys } from '../../../../../../../../enums/queryKeys'
import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../../../hooks'
import { CustomEventType, useCustomEventListener } from '../../../../../../../../hooks/useCustomEventListener'
import { getCurrentLocale } from '../../../../../../../../utils/getCurrentLocale'
import { ExpensesTrackingEvents } from '../../../../../../../receipts/enums/tracking'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../../../contexts'
import { useSandboxGuardedHandler } from '../../../../hooks/useSandboxGuardedHandler'
import { SandboxBlockedTooltip } from '../../../SandboxBlockedTooltip'
import { getBillFormFromBankTransaction } from './utils/getBillFormFromBankTransaction'

export const NewReceipt = () => {
  const [initialFormValues, setInitialFormValues] = useState<BillFormPartial>()
  const [isPreparing, setIsPreparing] = useState(false)
  const [showBillEditor, setShowBillEditor] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { user } = useCurrentUser()
  const isSandboxMode = !!useSandboxModeContext()
  const { currency, selectedTransaction, setDrawerCloseBlocked, selectCreatedBookkeepingItem } =
    useBankTransactionDrawer()

  const handleOpenCreateReceiptReal = useCallback(async () => {
    track(ExpensesTrackingEvents.CreatePurchaseClicked, {
      source: 'Transaction view',
    })

    if (!organization?.id || !selectedTransaction) {
      return
    }

    setIsPreparing(true)
    setDrawerCloseBlocked(true)

    const formValues = await getBillFormFromBankTransaction({
      chartOfAccountId: organization.chartOfAccountId,
      currency,
      locale: getCurrentLocale(),
      organizationCountryCode: organization.countryId,
      organizationId: organization.id,
      transaction: selectedTransaction,
      userId: user?.id,
      userLocale: user?.locale,
    })

    setInitialFormValues(formValues)
    setShowBillEditor(true)
    setIsPreparing(false)
  }, [currency, organization, selectedTransaction, setDrawerCloseBlocked, track, user?.id, user?.locale])

  const handleOpenCreateReceipt = useSandboxGuardedHandler(isSandboxMode, handleOpenCreateReceiptReal)

  const handleCloseCreateReceipt = useCallback(() => {
    setInitialFormValues(undefined)
    setShowBillEditor(false)
    setDrawerCloseBlocked(false)
  }, [setDrawerCloseBlocked])

  const handleBillCreated = useCallback(
    async (event: CustomEventType<BillCreatedEventDetail>) => {
      const billId = event.detail?.billId

      try {
        if (billId) {
          await Promise.all([
            queryClient.invalidateQueries(QueryKeys.Bills),
            queryClient.invalidateQueries(QueryKeys.Contacts),
          ])
          selectCreatedBookkeepingItem(billId)
        }
      } finally {
        handleCloseCreateReceipt()
      }
    },
    [handleCloseCreateReceipt, selectCreatedBookkeepingItem],
  )

  useCustomEventListener(CustomEvent.BillCreated, handleBillCreated)

  return (
    <>
      {showBillEditor && (
        <FullScreenOverlay bgColor={Color.White}>
          <BillEditable
            initialFormValues={initialFormValues}
            modalDisplayMode
            onBackButtonClick={handleCloseCreateReceipt}
          />
        </FullScreenOverlay>
      )}
      <SandboxBlockedTooltip>
        <ButtonProtected
          fullWidth
          icon="plus"
          loading={isPreparing}
          onClick={handleOpenCreateReceipt}
          scopes={Scope.BankReconciliationWrite}
          trackingContext={TrackingContext.BankTransactions}
          variant="tonal"
        >
          {t('bank_transactions.drawer.import_receipt_title')}
        </ButtonProtected>
      </SandboxBlockedTooltip>
    </>
  )
}
